/**
 * OneVeracity Design Tokens — Colors
 *
 * Single source of truth for all color values in the application.
 * Aligned to UX_GUIDE_UNIFIED.md and OneVeracity Brand Guidelines v2.0.
 *
 * IMPORTANT: Veracity Orange (#D6461D) must always be used at 100% opacity.
 * Secondary colors may use opacity in 10% increments.
 */

// =============================================================================
// Brand Palette
// =============================================================================

export const brand = {
  veracityOrange: '#D6461D',
  daybreakAmber: '#F79E31',
  morningCoral: '#EE6352',
  horizonBlue: '#A4C0C1',
  twilightPlum: '#553746',
  duskBlue: '#3F4C5C',
  clarityGreen: '#597B4B',
  midnightCharcoal: '#1C1919',
  gentleHaze: '#FFE6C8',
  firstLight: '#FFF1E0',
} as const;

// =============================================================================
// Light Theme Palette
// =============================================================================

export const light = {
  bgBase: '#FFF1E0',       // First Light — page background
  bgSurface: '#ffffff',    // Cards, panels
  bgElevated: '#FFE6C8',   // Gentle Haze — hover, selected rows
  border: '#e8d5bc',
  textPrimary: '#1C1919',  // Midnight Charcoal
  textSecondary: '#6b5240',
  textMuted: '#a8896b',
  accent: '#D6461D',
  accentSubtle: '#fce8e2',
} as const;

// =============================================================================
// Dark Theme Palette
// =============================================================================

export const dark = {
  bgBase: '#1C1919',       // Midnight Charcoal
  bgSurface: '#2a2523',    // Warm-tinted dark
  bgElevated: '#363029',   // Hover, tooltips, selected
  border: '#42392f',
  textPrimary: '#FFF1E0',  // First Light
  textSecondary: '#c4a98a',
  textMuted: '#7a6450',
  accent: '#D6461D',
  accentSubtle: '#3d1c0f',
} as const;

// =============================================================================
// Semantic Status Colors
// =============================================================================

export const status = {
  positive: '#597B4B',     // Clarity Green — favorable
  negative: '#EE6352',     // Morning Coral — unfavorable/error
  warning: '#F79E31',      // Daybreak Amber — caution
  neutralDelta: '#7a6450', // Muted warm — no-judgment
  info: '#3F4C5C',         // Dusk Blue
} as const;

// =============================================================================
// Chart Series (fixed order — never skip or reorder)
// =============================================================================

export const chartSeries = [
  '#D6461D', // Veracity Orange (primary)
  '#F79E31', // Daybreak Amber
  '#A4C0C1', // Horizon Blue
  '#553746', // Twilight Plum
  '#3F4C5C', // Dusk Blue at 60% — use with opacity
] as const;

// =============================================================================
// Unified COLORS object (backward-compatible import for page components)
// =============================================================================

export const COLORS = {
  // Brand
  primary: brand.veracityOrange,
  primaryDark: '#B03818',

  // Semantic
  success: status.positive,
  error: status.negative,
  warning: status.warning,

  // Light theme surfaces
  white: '#FFFFFF',
  bgBase: light.bgBase,
  bgSurface: light.bgSurface,
  bgElevated: light.bgElevated,

  // Light theme text (warm neutrals replacing cool grays)
  textPrimary: light.textPrimary,
  textSecondary: light.textSecondary,
  textMuted: light.textMuted,
  disabled: '#c4a98a',
  disabledText: '#a8896b',

  // Light theme borders
  border: light.border,
  borderSubtle: '#f0dcc4',

  // Secondary brand
  secondary: brand.daybreakAmber,
  teal: brand.horizonBlue,
  plum: brand.twilightPlum,
  duskBlue: brand.duskBlue,
  green: brand.clarityGreen,

  // Surfaces
  surface: light.bgSurface,
  surfaceHover: light.bgElevated,

  // Drag state
  dragOver: light.accentSubtle,
} as const;
